import {
  Alert,
  AlertIcon,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberInput,
  NumberInputField,
  Select,
  Text,
  Textarea,
  useToast,
  VStack,
} from '@elkaso-app/web-design';
import { useCreateSourceBrandLocationApi } from 'apis/crm-source-brand-locations/use-create-source-brand-location-api';
import React, { useState } from 'react';

interface CreateSourceBrandLocationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  brandId: number | '';
  geoLocationId: number | '';
  sourcePlatform: string;
  sourceExternalId: string;
  sourceUrl: string;
  name: string;
  address: string;
  coordinates: string;
  rating: number | '';
  ratingsTotal: number | '';
  reviewsTotal: number | '';
  priceLevel: number | '';
  avgCostPerCouple: number | '';
  avgCostCurrency: string;
  businessStatus: string;
  phoneNumber: string;
  website: string;
}

interface FormErrors {
  brandId?: string;
  geoLocationId?: string;
  sourcePlatform?: string;
  sourceExternalId?: string;
  sourceUrl?: string;
  name?: string;
  address?: string;
  coordinates?: string;
  rating?: string;
  ratingsTotal?: string;
  reviewsTotal?: string;
  priceLevel?: string;
  avgCostPerCouple?: string;
  avgCostCurrency?: string;
  businessStatus?: string;
  phoneNumber?: string;
  website?: string;
}

const CreateSourceBrandLocationModal: React.FC<CreateSourceBrandLocationModalProps> = ({ isOpen, onClose }) => {
  const toast = useToast();
  const createMutation = useCreateSourceBrandLocationApi();

  const [formData, setFormData] = useState<FormData>({
    brandId: '',
    geoLocationId: '',
    sourcePlatform: '',
    sourceExternalId: '',
    sourceUrl: '',
    name: '',
    address: '',
    coordinates: '',
    rating: '',
    ratingsTotal: '',
    reviewsTotal: '',
    priceLevel: '',
    avgCostPerCouple: '',
    avgCostCurrency: '',
    businessStatus: '',
    phoneNumber: '',
    website: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.brandId) newErrors.brandId = 'Brand ID is required';
    if (!formData.sourcePlatform) newErrors.sourcePlatform = 'Source platform is required';
    if (!formData.sourceExternalId) newErrors.sourceExternalId = 'Source external ID is required';
    if (!formData.name) newErrors.name = 'Location name is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields',
        status: 'error',
      });
      return;
    }

    try {
      const payload = {
        brandId: Number(formData.brandId),
        ...(formData.geoLocationId && { geoLocationId: Number(formData.geoLocationId) }),
        sourcePlatform: formData.sourcePlatform,
        sourceExternalId: formData.sourceExternalId,
        ...(formData.sourceUrl && { sourceUrl: formData.sourceUrl }),
        name: formData.name,
        ...(formData.address && { address: formData.address }),
        ...(formData.coordinates && { coordinates: formData.coordinates }),
        ...(formData.rating && { rating: Number(formData.rating) }),
        ...(formData.ratingsTotal && { ratingsTotal: Number(formData.ratingsTotal) }),
        ...(formData.reviewsTotal && { reviewsTotal: Number(formData.reviewsTotal) }),
        ...(formData.priceLevel && { priceLevel: Number(formData.priceLevel) }),
        ...(formData.avgCostPerCouple && { avgCostPerCouple: Number(formData.avgCostPerCouple) }),
        ...(formData.avgCostCurrency && { avgCostCurrency: formData.avgCostCurrency }),
        ...(formData.businessStatus && { businessStatus: formData.businessStatus }),
        ...(formData.phoneNumber && { phoneNumber: formData.phoneNumber }),
        ...(formData.website && { website: formData.website }),
      };

      await createMutation.mutateAsync(payload);
      onClose();
      resetForm();
    } catch (error) {
      // Error is handled in the API hook
    }
  };

  const resetForm = () => {
    setFormData({
      brandId: '',
      geoLocationId: '',
      sourcePlatform: '',
      sourceExternalId: '',
      sourceUrl: '',
      name: '',
      address: '',
      coordinates: '',
      rating: '',
      ratingsTotal: '',
      reviewsTotal: '',
      priceLevel: '',
      avgCostPerCouple: '',
      avgCostCurrency: '',
      businessStatus: '',
      phoneNumber: '',
      website: '',
    });
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size='xl'>
      <ModalOverlay />
      <ModalContent maxW='800px'>
        <ModalHeader>Create Source Brand Location</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={4} align='stretch'>
            <Alert status='info'>
              <AlertIcon />
              Fill in the required fields (*) to create a new source brand location.
            </Alert>

            {/* Basic Information */}
            <VStack spacing={4} align='stretch' p={4} border='1px' borderColor='gray.200' borderRadius='md'>
              <Text fontWeight='bold' color='gray.700'>
                Basic Information
              </Text>

              <HStack spacing={4}>
                <FormControl isRequired isInvalid={!!errors.brandId}>
                  <FormLabel>Brand ID</FormLabel>
                  <NumberInput
                    value={formData.brandId}
                    onChange={(_, valueAsNumber) =>
                      setFormData((prev) => ({ ...prev, brandId: isNaN(valueAsNumber) ? '' : valueAsNumber }))
                    }>
                    <NumberInputField placeholder='Enter brand ID' />
                  </NumberInput>
                </FormControl>

                <FormControl>
                  <FormLabel>Geo Location ID</FormLabel>
                  <NumberInput
                    value={formData.geoLocationId}
                    onChange={(_, valueAsNumber) =>
                      setFormData((prev) => ({ ...prev, geoLocationId: isNaN(valueAsNumber) ? '' : valueAsNumber }))
                    }>
                    <NumberInputField placeholder='Enter geo location ID' />
                  </NumberInput>
                </FormControl>
              </HStack>

              <HStack spacing={4}>
                <FormControl isRequired isInvalid={!!errors.sourcePlatform}>
                  <FormLabel>Source Platform</FormLabel>
                  <Input
                    value={formData.sourcePlatform}
                    onChange={(e) => setFormData((prev) => ({ ...prev, sourcePlatform: e.target.value }))}
                    placeholder='e.g., Google, Yelp, TripAdvisor'
                  />
                </FormControl>

                <FormControl isRequired isInvalid={!!errors.sourceExternalId}>
                  <FormLabel>Source External ID</FormLabel>
                  <Input
                    value={formData.sourceExternalId}
                    onChange={(e) => setFormData((prev) => ({ ...prev, sourceExternalId: e.target.value }))}
                    placeholder='External platform ID'
                  />
                </FormControl>
              </HStack>

              <FormControl isRequired isInvalid={!!errors.name}>
                <FormLabel>Location Name</FormLabel>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder='Enter location name'
                />
              </FormControl>

              <FormControl>
                <FormLabel>Source URL</FormLabel>
                <Input
                  value={formData.sourceUrl}
                  onChange={(e) => setFormData((prev) => ({ ...prev, sourceUrl: e.target.value }))}
                  placeholder='URL to the source platform page'
                />
              </FormControl>
            </VStack>

            {/* Location Details */}
            <VStack spacing={4} align='stretch' p={4} border='1px' borderColor='gray.200' borderRadius='md'>
              <Text fontWeight='bold' color='gray.700'>
                Location Details
              </Text>

              <FormControl>
                <FormLabel>Address</FormLabel>
                <Textarea
                  value={formData.address}
                  onChange={(e) => setFormData((prev) => ({ ...prev, address: e.target.value }))}
                  placeholder='Enter full address'
                  rows={3}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Coordinates</FormLabel>
                <Input
                  value={formData.coordinates}
                  onChange={(e) => setFormData((prev) => ({ ...prev, coordinates: e.target.value }))}
                  placeholder='lat,lng (e.g., 25.2048,55.2708)'
                />
              </FormControl>

              <HStack spacing={4}>
                <FormControl>
                  <FormLabel>Phone Number</FormLabel>
                  <Input
                    value={formData.phoneNumber}
                    onChange={(e) => setFormData((prev) => ({ ...prev, phoneNumber: e.target.value }))}
                    placeholder='Contact phone number'
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Website</FormLabel>
                  <Input
                    value={formData.website}
                    onChange={(e) => setFormData((prev) => ({ ...prev, website: e.target.value }))}
                    placeholder='Website URL'
                  />
                </FormControl>
              </HStack>
            </VStack>

            {/* Ratings & Business Info */}
            <VStack spacing={4} align='stretch' p={4} border='1px' borderColor='gray.200' borderRadius='md'>
              <Text fontWeight='bold' color='gray.700'>
                Ratings & Business Information
              </Text>

              <HStack spacing={4}>
                <FormControl>
                  <FormLabel>Rating</FormLabel>
                  <NumberInput
                    value={formData.rating}
                    onChange={(_, valueAsNumber) =>
                      setFormData((prev) => ({ ...prev, rating: isNaN(valueAsNumber) ? '' : valueAsNumber }))
                    }
                    min={0}
                    max={5}
                    step={0.1}>
                    <NumberInputField placeholder='0.0' />
                  </NumberInput>
                </FormControl>

                <FormControl>
                  <FormLabel>Ratings Total</FormLabel>
                  <NumberInput
                    value={formData.ratingsTotal}
                    onChange={(_, valueAsNumber) =>
                      setFormData((prev) => ({ ...prev, ratingsTotal: isNaN(valueAsNumber) ? '' : valueAsNumber }))
                    }>
                    <NumberInputField placeholder='Total number of ratings' />
                  </NumberInput>
                </FormControl>

                <FormControl>
                  <FormLabel>Reviews Total</FormLabel>
                  <NumberInput
                    value={formData.reviewsTotal}
                    onChange={(_, valueAsNumber) =>
                      setFormData((prev) => ({ ...prev, reviewsTotal: isNaN(valueAsNumber) ? '' : valueAsNumber }))
                    }>
                    <NumberInputField placeholder='Total number of reviews' />
                  </NumberInput>
                </FormControl>
              </HStack>

              <HStack spacing={4}>
                <FormControl>
                  <FormLabel>Price Level</FormLabel>
                  <Select
                    value={formData.priceLevel}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, priceLevel: e.target.value ? Number(e.target.value) : '' }))
                    }>
                    <option value=''>Select price level</option>
                    <option value='1'>$ (Inexpensive)</option>
                    <option value='2'>$$ (Moderate)</option>
                    <option value='3'>$$$ (Expensive)</option>
                    <option value='4'>$$$$ (Very Expensive)</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>Avg Cost Per Couple</FormLabel>
                  <NumberInput
                    value={formData.avgCostPerCouple}
                    onChange={(_, valueAsNumber) =>
                      setFormData((prev) => ({ ...prev, avgCostPerCouple: isNaN(valueAsNumber) ? '' : valueAsNumber }))
                    }>
                    <NumberInputField placeholder='Average cost' />
                  </NumberInput>
                </FormControl>

                <FormControl>
                  <FormLabel>Currency</FormLabel>
                  <Input
                    value={formData.avgCostCurrency}
                    onChange={(e) => setFormData((prev) => ({ ...prev, avgCostCurrency: e.target.value }))}
                    placeholder='AED, USD, etc.'
                  />
                </FormControl>
              </HStack>

              <FormControl>
                <FormLabel>Business Status</FormLabel>
                <Select
                  value={formData.businessStatus}
                  onChange={(e) => setFormData((prev) => ({ ...prev, businessStatus: e.target.value }))}>
                  <option value=''>Select business status</option>
                  <option value='OPERATIONAL'>Operational</option>
                  <option value='CLOSED_TEMPORARILY'>Closed Temporarily</option>
                  <option value='CLOSED_PERMANENTLY'>Closed Permanently</option>
                </Select>
              </FormControl>
            </VStack>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant='ghost' mr={3} onClick={handleClose} disabled={createMutation.isLoading}>
            Cancel
          </Button>
          <Button
            colorScheme='blue'
            onClick={handleSubmit}
            isLoading={createMutation.isLoading}
            loadingText='Creating...'>
            Create Location
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CreateSourceBrandLocationModal;
